import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { ScrollArea } from "../components/ui/scroll-area";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Checkbox } from "../components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  ArrowLeft,
  User,
  Users,
  MessageCircle,
  Send,
  Paperclip,
  FileText,
  Image,
  File,
  Download,
  X,
  Calendar,
  FolderOpen,
  HandHelping,
  Clock,
  CheckCircle2,
  AlertCircle,
  ZoomIn,
  ExternalLink,
  Trash2,
  CheckSquare
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResponsibilityDetail = () => {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const messagesEndRef = useRef(null);
  
  const [task, setTask] = useState(null);
  const [assignedUser, setAssignedUser] = useState(null);
  const [assignedAssets, setAssignedAssets] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const api = axios.create({
    baseURL: API_URL,
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
  });

  useEffect(() => {
    fetchData();
  }, [taskId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      // Fetch task details
      const taskRes = await api.get(`/api/tasks/${taskId}`);
      setTask(taskRes.data);

      // Fetch assigned user if exists
      if (taskRes.data.assigned_to) {
        try {
          const usersRes = await api.get("/api/employees");
          const assigned = usersRes.data.find(u => u.id === taskRes.data.assigned_to);
          setAssignedUser(assigned || null);
        } catch (e) {
          console.error("Failed to fetch assigned user");
        }
      }

      // Fetch assigned assets
      if (taskRes.data.assigned_assets?.length > 0) {
        try {
          const assetsRes = await api.get("/api/assets");
          const taskAssets = assetsRes.data.filter(a => 
            taskRes.data.assigned_assets.includes(a.id)
          );
          setAssignedAssets(taskAssets);
        } catch (e) {
          console.error("Failed to fetch assets");
        }
      }

      // Fetch messages
      try {
        const messagesRes = await api.get(`/api/messages/task/${taskId}`);
        setMessages(messagesRes.data);
      } catch (e) {
        console.error("Failed to fetch messages");
      }
    } catch (error) {
      toast.error("Failed to load responsibility details");
      navigate(-1);
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    
    setIsSending(true);
    try {
      const res = await api.post("/api/messages", {
        task_id: taskId,
        content: newMessage.trim()
      });
      setMessages([...messages, res.data]);
      setNewMessage("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send message");
    } finally {
      setIsSending(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 25 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 25MB");
      return;
    }
    
    const allowedTypes = [
      'image/jpeg', 'image/png', 'image/gif', 'image/webp',
      'application/pdf',
      'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      toast.error("File type not allowed. Please upload images, PDFs, Word docs, or Excel files.");
      return;
    }
    
    setSelectedFile(file);
  };

  const uploadFile = async () => {
    if (!selectedFile) return;
    
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('task_id', taskId);
      formData.append('content', newMessage.trim() || `Shared a file: ${selectedFile.name}`);
      
      const res = await api.post("/api/messages/upload", formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setMessages([...messages, res.data]);
      setSelectedFile(null);
      setNewMessage("");
      toast.success("File uploaded successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload file");
    } finally {
      setIsUploading(false);
    }
  };

  // Delete single message
  const deleteMessage = async (messageId) => {
    setIsDeleting(true);
    try {
      await api.delete(`/api/messages/${messageId}`);
      setMessages(messages.filter(m => m.id !== messageId));
      toast.success("Note deleted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete note");
    } finally {
      setIsDeleting(false);
    }
  };

  // Delete selected messages
  const deleteSelectedMessages = async () => {
    if (selectedMessages.length === 0) return;
    
    setIsDeleting(true);
    try {
      await Promise.all(selectedMessages.map(id => api.delete(`/api/messages/${id}`)));
      setMessages(messages.filter(m => !selectedMessages.includes(m.id)));
      setSelectedMessages([]);
      setIsSelectMode(false);
      toast.success(`Deleted ${selectedMessages.length} note(s)`);
    } catch (error) {
      toast.error("Failed to delete some notes");
    } finally {
      setIsDeleting(false);
    }
  };

  // Delete all messages for this task
  const deleteAllMessages = async () => {
    setIsDeleting(true);
    try {
      await api.delete(`/api/tasks/${taskId}/messages`);
      setMessages([]);
      setSelectedMessages([]);
      setIsSelectMode(false);
      toast.success("All notes deleted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete notes");
    } finally {
      setIsDeleting(false);
    }
  };

  // Toggle message selection
  const toggleMessageSelection = (messageId) => {
    setSelectedMessages(prev => 
      prev.includes(messageId) 
        ? prev.filter(id => id !== messageId)
        : [...prev, messageId]
    );
  };

  // Select all messages
  const selectAllMessages = () => {
    if (selectedMessages.length === messages.length) {
      setSelectedMessages([]);
    } else {
      setSelectedMessages(messages.map(m => m.id));
    }
  };

  const getFileIcon = (contentType) => {
    if (contentType?.startsWith('image/')) return <Image className="w-4 h-4" />;
    if (contentType?.includes('pdf')) return <FileText className="w-4 h-4" />;
    return <File className="w-4 h-4" />;
  };

  const isImageFile = (contentType) => {
    return contentType?.startsWith('image/');
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const canParticipate = () => {
    if (!task || !user) return false;
    return task.assigned_to === user.id || task.assigned_assets?.includes(user.id) || user.role === 'admin';
  };

  const backPath = user?.role === 'admin' ? '/admin/tasks' : '/dashboard/tasks';

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-2">Responsibility Not Found</h2>
          <Button onClick={() => navigate(backPath)}>Go Back</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(backPath)} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {task.title}
                </h1>
                <Badge variant={task.status === 'assigned' ? 'default' : 'secondary'}>
                  {task.status === 'assigned' ? 'Assigned' : 'Open'}
                </Badge>
              </div>
              <p className="text-muted-foreground flex items-center gap-2 mt-1">
                <FolderOpen className="w-4 h-4" />
                {task.category}
              </p>
            </div>
            <Link to={backPath}>
              <img src="/images/logo.png" alt="Future Owners" className="w-10 h-10 object-contain" />
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Details & People */}
          <div className="space-y-6">
            {/* Description */}
            <Card data-testid="responsibility-description">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  Description
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground whitespace-pre-wrap">
                  {task.description || "No description provided."}
                </p>
                {task.created_at && (
                  <p className="text-xs text-muted-foreground mt-4 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    Created {formatDate(task.created_at)}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Assigned Future Owner */}
            <Card data-testid="assigned-owner-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="w-5 h-5 text-primary" />
                  Assigned Future Owner
                </CardTitle>
              </CardHeader>
              <CardContent>
                {assignedUser ? (
                  <div className="flex items-center gap-3 p-3 bg-secondary/30 rounded-lg">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">
                        {assignedUser.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium">{assignedUser.name}</p>
                      <p className="text-sm text-muted-foreground">{assignedUser.email}</p>
                    </div>
                    <CheckCircle2 className="w-5 h-5 text-green-500 ml-auto" />
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted-foreground">
                    <User className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No one assigned yet</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Assigned Assets */}
            <Card data-testid="assigned-assets-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <HandHelping className="w-5 h-5 text-orange-500" />
                  Assigned Helpers ({assignedAssets.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {assignedAssets.length > 0 ? (
                  <div className="space-y-2">
                    {assignedAssets.map((asset) => (
                      <div key={asset.id} className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg">
                        <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                          <span className="text-orange-600 font-semibold">
                            {asset.name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium">{asset.name}</p>
                          <p className="text-sm text-muted-foreground">{asset.email}</p>
                        </div>
                        <Badge variant="outline" className="ml-auto text-orange-600 border-orange-300">
                          Asset
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted-foreground">
                    <HandHelping className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No helpers assigned</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Communication & Files */}
          <div className="lg:col-span-2">
            <Card className="h-[calc(100vh-220px)] flex flex-col" data-testid="communication-card">
              <CardHeader className="pb-3 border-b">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MessageCircle className="w-5 h-5 text-primary" />
                    Communication & Documentation
                    <Badge variant="secondary" className="ml-2">
                      {messages.length} {messages.length === 1 ? 'entry' : 'entries'}
                    </Badge>
                  </CardTitle>
                  
                  {/* Admin Delete Controls */}
                  {user?.role === 'admin' && messages.length > 0 && (
                    <div className="flex items-center gap-2">
                      {isSelectMode ? (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={selectAllMessages}
                            className="text-xs"
                          >
                            <CheckSquare className="w-4 h-4 mr-1" />
                            {selectedMessages.length === messages.length ? 'Deselect All' : 'Select All'}
                          </Button>
                          <span className="text-xs text-muted-foreground">
                            {selectedMessages.length} selected
                          </span>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="destructive"
                                size="sm"
                                disabled={selectedMessages.length === 0 || isDeleting}
                                className="text-xs"
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Delete Selected
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete {selectedMessages.length} note(s)?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This action cannot be undone. Any attached files will also be deleted.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={deleteSelectedMessages} className="bg-destructive hover:bg-destructive/90">
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setIsSelectMode(false); setSelectedMessages([]); }}
                            className="text-xs"
                          >
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setIsSelectMode(true)}
                            className="text-xs"
                          >
                            <CheckSquare className="w-4 h-4 mr-1" />
                            Select
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-xs text-destructive hover:text-destructive"
                                disabled={isDeleting}
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Delete All
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete all {messages.length} note(s)?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently delete all notes and attached files for this responsibility. This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={deleteAllMessages} className="bg-destructive hover:bg-destructive/90">
                                  Delete All
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </CardHeader>
              
              <CardContent className="flex-1 flex flex-col p-0 overflow-hidden">
                {/* Messages Area */}
                <ScrollArea className="flex-1 p-4">
                  {messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-muted-foreground py-12">
                      <MessageCircle className="w-16 h-16 mb-4 opacity-30" />
                      <p className="text-lg font-medium">No communication yet</p>
                      <p className="text-sm">Start documenting progress, share files, and communicate here.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {messages.map((msg) => (
                        <div 
                          key={msg.id} 
                          className={`flex items-start gap-2 ${msg.is_own_message ? 'justify-end' : 'justify-start'}`}
                          data-testid={`message-${msg.id}`}
                        >
                          {/* Checkbox for select mode (admin only) */}
                          {isSelectMode && user?.role === 'admin' && (
                            <div className="pt-4">
                              <Checkbox
                                checked={selectedMessages.includes(msg.id)}
                                onCheckedChange={() => toggleMessageSelection(msg.id)}
                                className="border-primary"
                              />
                            </div>
                          )}
                          
                          <div className={`max-w-[80%] relative group ${
                            msg.is_own_message 
                              ? 'bg-primary text-primary-foreground' 
                              : 'bg-secondary'
                          } rounded-lg p-4 shadow-sm`}>
                            {/* Individual delete button for admin (when not in select mode) */}
                            {!isSelectMode && user?.role === 'admin' && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <button 
                                    className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-destructive text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md hover:bg-destructive/90"
                                    title="Delete note"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete this note?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This action cannot be undone. {msg.attachment ? 'The attached file will also be deleted.' : ''}
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => deleteMessage(msg.id)} className="bg-destructive hover:bg-destructive/90">
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                            
                            {!msg.is_own_message && (
                              <div className="flex items-center gap-2 mb-2">
                                <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold">
                                  {msg.sender_name?.charAt(0).toUpperCase()}
                                </div>
                                <span className="text-sm font-medium opacity-90">{msg.sender_name}</span>
                              </div>
                            )}
                            
                            <p className="whitespace-pre-wrap">{msg.content}</p>
                            
                            {/* Attachment */}
                            {msg.attachment && (
                              <div 
                                className="mt-3 bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden"
                                data-testid="attachment-box"
                              >
                                {/* Image Preview for image files */}
                                {isImageFile(msg.attachment.content_type) && (
                                  <div 
                                    className="relative group cursor-pointer"
                                    onClick={() => setLightboxImage(`${API_URL}${msg.attachment.url}`)}
                                  >
                                    <img 
                                      src={`${API_URL}${msg.attachment.url}`}
                                      alt={msg.attachment.original_filename}
                                      className="w-full max-h-64 object-contain bg-slate-50"
                                      loading="lazy"
                                    />
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                                      <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-2">
                                        <ZoomIn className="w-5 h-5 text-slate-700" />
                                      </div>
                                    </div>
                                  </div>
                                )}
                                
                                {/* File info and download button */}
                                <div className="p-3 flex items-center gap-3">
                                  <a 
                                    href={`${API_URL}${msg.attachment.url}`}
                                    download={msg.attachment.original_filename}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-3 flex-1 text-slate-800 hover:text-slate-900 no-underline"
                                    data-testid={`attachment-${msg.attachment.id}`}
                                  >
                                    <span className="text-slate-500 shrink-0">
                                      {getFileIcon(msg.attachment.content_type)}
                                    </span>
                                    <span className="flex-1 min-w-0">
                                      <span className="block text-sm font-semibold text-slate-700 truncate">{msg.attachment.original_filename}</span>
                                      <span className="block text-xs text-slate-500">{formatFileSize(msg.attachment.size)}</span>
                                    </span>
                                    <span className="flex items-center gap-1 px-3 py-1.5 bg-primary text-white rounded text-xs font-medium shrink-0 hover:bg-primary/90 transition-colors">
                                      <Download className="w-3 h-3" />
                                      Download
                                    </span>
                                  </a>
                                  {isImageFile(msg.attachment.content_type) && (
                                    <button 
                                      onClick={() => setLightboxImage(`${API_URL}${msg.attachment.url}`)}
                                      className="p-1.5 rounded hover:bg-slate-100 transition-colors"
                                      title="View full size"
                                    >
                                      <ExternalLink className="w-4 h-4 text-slate-500" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            <p className="text-xs opacity-60 mt-2 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDate(msg.created_at)}
                            </p>
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </ScrollArea>

                {/* Input Area */}
                {canParticipate() ? (
                  <div className="p-4 border-t bg-secondary/10">
                    {/* Selected File Preview */}
                    {selectedFile && (
                      <div className="flex items-center gap-2 p-3 mb-3 bg-primary/10 rounded-lg">
                        {getFileIcon(selectedFile.type)}
                        <span className="text-sm flex-1 truncate font-medium">{selectedFile.name}</span>
                        <span className="text-xs text-muted-foreground">{formatFileSize(selectedFile.size)}</span>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setSelectedFile(null)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                    
                    <div className="flex gap-2">
                      <input
                        type="file"
                        id="file-upload-detail"
                        className="hidden"
                        onChange={handleFileSelect}
                        accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx"
                      />
                      <Button 
                        variant="outline" 
                        size="icon"
                        onClick={() => document.getElementById('file-upload-detail').click()}
                        disabled={isSending || isUploading}
                        title="Attach file"
                        data-testid="attach-file-btn"
                      >
                        <Paperclip className="w-5 h-5" />
                      </Button>
                      <Input
                        placeholder="Add a note, update, or documentation..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (selectedFile ? uploadFile() : sendMessage())}
                        disabled={isSending || isUploading}
                        className="flex-1"
                        data-testid="message-input"
                      />
                      <Button 
                        onClick={selectedFile ? uploadFile : sendMessage} 
                        disabled={(isSending || isUploading) || (!selectedFile && !newMessage.trim())}
                        data-testid="send-btn"
                      >
                        {isUploading ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        ) : (
                          <>
                            <Send className="w-5 h-5 mr-2" />
                            {selectedFile ? 'Upload' : 'Send'}
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 border-t bg-secondary/10 text-center text-muted-foreground">
                    <p>Only assigned participants can add to this communication log.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Image Lightbox Modal */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            onClick={() => setLightboxImage(null)}
          >
            <X className="w-6 h-6" />
          </button>
          <img 
            src={lightboxImage}
            alt="Full size preview"
            className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            <a
              href={lightboxImage}
              download
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-full text-sm font-medium hover:bg-primary/90 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <Download className="w-4 h-4" />
              Download
            </a>
            <button
              onClick={() => setLightboxImage(null)}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-full text-sm font-medium hover:bg-white/20 transition-colors"
            >
              <X className="w-4 h-4" />
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResponsibilityDetail;
